import { DELETION_FLAG, parse, toPathSafeString } from "@packages/core";
import { useForm, useSelector } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useRouter } from "@tanstack/react-router";
import Encoding from "encoding-japanese";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuDownload } from "react-icons/lu";
import { toast } from "sonner";
import { Temporal } from "temporal-polyfill";
import * as v from "valibot";

import {
  ButtonStyle2,
  FileUpload,
  PageName,
  PaperStyle,
  Spinner,
  TypographyStyle,
} from "#components";
import {
  BedBatchRegistrationHeaderRows,
  BedBatchRegistrationRowSchema,
  toBedBatchRegistrationRow,
} from "#models";
import {
  $confirm,
  modifyBed,
  registerBed,
  resetBedInfiniteQueries,
  saveAsFile,
  useBedInfiniteQuery,
  useFacilityQuery,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/beds/batch-registration",
)({ component: RouteComponent });

function RouteComponent() {
  const { facilityKey } = Route.useParams();

  const router = useRouter();
  const { t } = useTranslation();

  const translate = useTranslate();
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const facilityQuery = useFacilityQuery(facilityKey);

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (bedInfiniteQuery.hasNextPage && !bedInfiniteQuery.isFetching) {
        bedInfiniteQuery.fetchNextPage();
      }
    },
    [bedInfiniteQuery],
  );

  const beds = useMemo(
    function () {
      return (
        bedInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.beds;
        }) ?? []
      );
    },
    [bedInfiniteQuery.data],
  );
  const { data: facility } = facilityQuery;

  const headerRows = useMemo(
    function () {
      return BedBatchRegistrationHeaderRows({ t });
    },
    [t],
  );

  const formSchema = useMemo(function () {
    return v.object({ files: v.array(v.file()) });
  }, []);

  const form = useForm({
    defaultValues: useMemo(function (): v.InferInput<typeof formSchema> {
      return { files: [] };
    }, []),
    async onSubmit({ value: input }) {
      let output;
      try {
        output = parse(formSchema, input);
        console.debug("フォームが送信されました。", { input, output });
      } catch (error) {
        console.error("フォームの解析に失敗しました。", { input, output }, error);
        toast.error(t("%FormParsingFailed.0%"));
        return;
      }

      const { files } = output;

      if (files.length === 0) {
        toast.warning(t("%FileNotSelected.0%"));
        return;
      }

      for (let f = 0; f < files.length; f++) {
        const file = files[f];

        let rows;
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          const characters = Encoding.codeToString(
            Encoding.convert(bytes, { from: "SJIS", to: "UNICODE" }),
          );
          rows = characters.split(/\r?\n/);
          console.info(file.name + "を読み込みました。", rows);
        } catch (error) {
          console.error(error);
          toast.error(t("%Import.1%", { $: { file } }));
          continue;
        }

        const toastId = toast.loading(
          t("%Import.10%", { $: { file } }) + " (" + 0 + " / " + rows.length + ")",
          { duration: Infinity },
        );

        let numberOfSuccessfulRows = headerRows.length;
        const failedRows = [];

        for (let r = headerRows.length; r < rows.length; r++) {
          const row = rows[r];

          toast.loading(
            t("%Import.10%", { $: { file } }) + " (" + (r + 1) + " / " + rows.length + ")",
            { id: toastId },
          );

          if (row.length === 0) {
            // 空行は無視する。
            numberOfSuccessfulRows++;
            continue;
          }

          let data;
          try {
            data = v.parse(BedBatchRegistrationRowSchema(), row.split(","));
          } catch (error) {
            console.error(error);
            failedRows.push(row + "," + t("%RowParsingFailed.0%"));
            continue;
          }

          console.info("行を解析しました。", data);

          try {
            if (data.bedKey) {
              console.debug("ベッド[" + data.bedKey + "]を変更します。", data);

              await modifyBed({ ...data, bedKey: data.bedKey });
            } else {
              console.debug("ベッドを作成します。", data);

              const {
                bed: { bedKey },
              } = await registerBed({ ...data, facilityKey });

              console.debug("ベッド[" + bedKey + "]を作成しました。");
            }
          } catch (error) {
            console.error(error);
            failedRows.push(row + "," + (error instanceof Error ? error.message : t("%Error.1%")));
            continue;
          }

          numberOfSuccessfulRows++;
        }

        if (failedRows.length > 0) {
          if (numberOfSuccessfulRows > 0) {
            toast.warning(
              t("%Import.7%", {
                $: { file, numberOfFailedRows: failedRows.length, numberOfSuccessfulRows },
              }),
              { id: toastId, duration: 5000 },
            );
          } else {
            toast.error(t("%Import.8%", { $: { file } }), { id: toastId, duration: 5000 });
          }
        } else {
          toast.success(t("%Import.6%", { $: { file } }), { id: toastId, duration: 5000 });
        }

        if (failedRows.length > 0) {
          saveAsFile(
            new Blob(
              [
                new Uint8Array(
                  Encoding.convert(
                    Encoding.stringToCode(headerRows.concat(failedRows).join("\r\n")),
                    { from: "UNICODE", to: "SJIS" },
                  ),
                ),
              ],
              { type: "text/csv" },
            ),
            toPathSafeString(file.name.replace(".csv", "") + " (" + t("%Failure.0%") + ").csv"),
          );
        }
      }

      resetBedInfiniteQueries();
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: formSchema },
  });

  const formStoreStateIsSubmitting = useSelector(form.store, function (state) {
    return state.isSubmitting;
  });

  useBlocker({
    async shouldBlockFn() {
      return (
        (!form.store.state.isDefaultValue || form.store.state.isSubmitting) &&
        !(await $confirm({
          color: "warning",
          content: t("%Form.1%"),
          no: t("%Stay.0%"),
          title: t("%Form.0%"),
          yes: t("%Leave.0%"),
        }))
      );
    },
  });

  return (
    <form
      className={css({
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "32px 24px" },
      })}
      data-main
      onSubmit={function (event) {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <PageName>
        {t("%BedBatchRegistration.0%") +
          " - " +
          (facility?.name ?? (!facilityQuery.isFetching ? t("%Unknown.0%") : t("%Loading.0%")))}
      </PageName>

      <div
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          maxWidth: "480px",
          padding: { smDown: "16px", sm: "16px 24px" },
          width: "stretch",
        })}
      >
        <h1
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
          })}
        >
          {translate("%AddBedViaCsv.0%")}
        </h1>
        <p className={TypographyStyle({ fontSize: "14px", marginTop: "16px" })}>
          {beds.length > 0 ? translate("%Import.12%") : translate("%Import.11%")}
        </p>
        {beds.length > 0 && (
          <p className={TypographyStyle({ color: "error", fontSize: "14px" })}>
            {translate("%Import.13%")}
          </p>
        )}

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          1.&nbsp;{beds.length > 0 ? translate("%Download.2%") : translate("%Download.1%")}
        </h2>
        <div className={css({ marginTop: "8px" })}>
          <button
            className={ButtonStyle2({ color: "gray", marginTop: "8px", width: "stretch" })}
            disabled={
              formStoreStateIsSubmitting || facilityQuery.isFetching || bedInfiniteQuery.isFetching
            }
            onClick={async function () {
              saveAsFile(
                new Blob(
                  [
                    new Uint8Array(
                      Encoding.convert(
                        Encoding.stringToCode(
                          headerRows
                            .concat(
                              beds.map(function (bed) {
                                return toBedBatchRegistrationRow(bed);
                              }),
                            )
                            .join("\r\n"),
                        ),
                        { from: "UNICODE", to: "SJIS" },
                      ),
                    ),
                  ],
                  { type: "text/csv" },
                ),
                toPathSafeString(
                  translate("%BedFileName.0%", {
                    $: {
                      name: facility?.name || facilityKey,
                      now: Temporal.Now.plainDateTimeISO("Asia/Tokyo"),
                    },
                  }) + ".csv",
                ),
              );
            }}
            type="button"
          >
            <LuDownload data-icon data-left />
            {translate("%Download.0%")}
          </button>
        </div>

        <h2 className={TypographyStyle({ fontSize: "14px", fontWeight: 700, marginTop: "16px" })}>
          2.&nbsp;{translate("%Upload.1%")}
        </h2>

        <form.Field name="files">
          {function (field) {
            return (
              <FileUpload
                accept="text/csv"
                acceptedFiles={field.state.value}
                className={css({ marginTop: "8px" })}
                disabled={formStoreStateIsSubmitting}
                onFileChange={function ({ acceptedFiles }) {
                  field.handleChange(acceptedFiles);
                }}
              />
            );
          }}
        </form.Field>

        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            marginTop: "16px",
          })}
        >
          <Link
            className={ButtonStyle2({ color: "white" })}
            disabled={formStoreStateIsSubmitting}
            onClick={function (event) {
              if (formStoreStateIsSubmitting) {
                event.preventDefault();
              } else if (router.history.canGoBack()) {
                event.preventDefault();
                router.history.back();
              }
            }}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/beds/allocation"
          >
            {t("%Cancel.0%")}
          </Link>

          <button
            className={ButtonStyle2({ color: "black" })}
            disabled={formStoreStateIsSubmitting}
            type="submit"
          >
            {formStoreStateIsSubmitting && (
              <Spinner color="#FAFAFA" data-left size="14px" type="a" />
            )}
            {t("%Import.0%")}
          </button>
        </div>
      </div>
    </form>
  );
}
